import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useAuth } from '@/auth/AuthProvider';
import { useAgencyId } from '@/data/rpc';
import { userChannel } from '@/lib/channels';
import { useLiveChannels } from './useLiveChannels';

/**
 * The rejection notice (design 4.2): `load_reject` publishes `load.rejected` on the load's DISPATCHER's
 * own channel only, so every signed-in office user listens on `user:<self>`. The payload names the load
 * and the reason; the number and the driver are re-read (the reject has already nulled the load's
 * driver, so the driver comes from the decline event's actor). The toast stays until dismissed or opened.
 * Mounted once in the shell.
 */
export function useRejectionNotices(): void {
  const { client, user } = useAuth();
  const agencyId = useAgencyId();
  const navigate = useNavigate();
  const { t } = useTranslation();
  // Subscribed on the user alone: waiting for the agency would drop a notice published while it loads
  // (a first subscription recovers nothing). The handler reads the agency of its render.
  const userId = user?.id ?? null;
  const channels = React.useMemo(() => (userId ? [userChannel(userId)] : []), [userId]);

  useLiveChannels(channels, (pub) => {
    if (pub.type !== 'load.rejected' || !agencyId) return;
    void (async () => {
      const [load, decline] = await Promise.all([
        client.from('loads').select('id, load_number').eq('agency_id', agencyId).eq('id', pub.loadId).maybeSingle(),
        client
          .from('load_events')
          .select('actor_user_id, at')
          .eq('agency_id', agencyId)
          .eq('load_id', pub.loadId)
          .eq('from_status', 'dispatched')
          .eq('to_status', 'booked')
          .order('at', { ascending: false })
          .limit(1),
      ]);
      const number = load.data?.load_number as string | undefined;
      if (!number) return; // not a load this agency reads
      const actor = (decline.data?.[0]?.actor_user_id as string | undefined) ?? null;
      let driver: string | null = null;
      if (actor) {
        const d = await client.from('drivers').select('name').eq('agency_id', agencyId).eq('user_id', actor).maybeSingle();
        driver = (d.data?.name as string | undefined) ?? null;
      }
      const values = { driver: driver ?? t('realtime.unknownDriver'), load: number, reason: pub.reason ?? '' };
      toast(pub.reason ? t('realtime.rejected', values) : t('realtime.rejectedNoReason', values), {
        duration: Infinity,
        action: { label: t('realtime.openLoad'), onClick: () => navigate(`/loads/${pub.loadId}`) },
      });
    })();
  });
}
